/**
 * Theme manager for loading and switching themes.
 * Dynamically discovers themes from the themes folder.
 */
import { Theme, ThemeConfig } from './types';
import { logWarning } from '../utils/errorLogging';

// Re-export Theme type for convenience
export type { Theme, ThemeConfig } from './types';

// Import all theme CSS files (they're scoped by data-theme attribute)
// Using require.context to discover all theme CSS files
// This ensures all theme CSS is available, but only the active theme's variables apply
// Using require.context to discover all theme CSS files
// IMPORTANT: require.context must be called with literal arguments for webpack to process it at build time
// Webpack replaces require.context calls with actual module imports during build
// Path './' is relative to this file's directory (themes/)
const cssContext = (require as any).context('./', true, /colors\.css$/);
cssContext.keys().forEach((key: string) => {
  cssContext(key);
});

// Dynamic theme discovery - finds all Footer.tsx files in theme subdirectories
// IMPORTANT: require.context must be called with literal arguments for webpack to process it at build time
const footerContext = (require as any).context('./', true, /Footer\.tsx$/);

/**
 * Theme registry - dynamically populated from filesystem
 */
const themes: Map<string, Theme> = new Map();

/**
 * Theme configurations - maps theme folder names to their configs
 * To add a new theme: create a folder with Footer.tsx and colors.css, then add config here
 */
const themeConfigs: Record<string, ThemeConfig> = {
  illinois: {
    id: 'illinois',
    name: 'University of Illinois',
    description: 'Default theme with University of Illinois colors',
    version: '1.0.0',
    primaryColor: '#FF5F00',
    secondaryColor: '#13294B',
    hasFooter: true,
  },
  neutral: {
    id: 'neutral',
    name: 'Neutral',
    description: 'Professional theme with neutral colors',
    version: '1.0.0',
    primaryColor: '#2563eb',
    secondaryColor: '#64748b',
    hasFooter: true,
  },
  professional: {
    id: 'professional',
    name: 'Professional',
    description: 'Sophisticated gray theme for professional environments',
    version: '1.0.0',
    primaryColor: '#374151',
    secondaryColor: '#6b7280',
    hasFooter: true,
  },
};

/**
 * Initialize themes from filesystem discovery
 */
function initializeThemes(): void {
  // Get all Footer.tsx files from theme subdirectories
  const footerFiles = footerContext.keys();
  
  footerFiles.forEach((filePath: string) => {
    // Extract theme ID from path (e.g., './illinois/Footer.tsx' -> 'illinois')
    const match = filePath.match(/\.\/([^/]+)\/Footer\.tsx$/);
    if (!match) return;
    
    const themeId = match[1];
    
    // Skip if not a valid theme folder (e.g., skip if it's in a nested folder)
    if (filePath.split('/').length !== 3) return;
    
    // Get theme config (or create default if not found)
    let config = themeConfigs[themeId];
    if (!config) {
      // Auto-generate config for discovered themes
      config = {
        id: themeId,
        name: themeId.charAt(0).toUpperCase() + themeId.slice(1),
        description: `Theme: ${themeId}`,
        version: '1.0.0',
        primaryColor: '#2563eb',
        secondaryColor: '#64748b',
        hasFooter: true,
      };
    }
    
    // Import footer component
    try {
      const footerModule = footerContext(filePath);
      
      // Try to find the Footer component export
      // It might be exported as default or as a named export (e.g., IllinoisFooter, NeutralFooter)
      let FooterComponent = footerModule.default;
      
      if (!FooterComponent) {
        // Try to find a named export that contains the theme name or "Footer"
        const exportKeys = Object.keys(footerModule).filter(key => 
          typeof footerModule[key] === 'function'
        );
        
        // Look for export matching theme name pattern (e.g., "IllinoisFooter" for "illinois")
        const themeNameCapitalized = themeId.charAt(0).toUpperCase() + themeId.slice(1);
        const footerKey = exportKeys.find(key => 
          key === `${themeNameCapitalized}Footer` || 
          key.toLowerCase() === `${themeId}footer` ||
          (key.toLowerCase().includes(themeId.toLowerCase()) && key.toLowerCase().includes('footer'))
        ) || exportKeys.find(key => key.toLowerCase().includes('footer'));
        
        if (footerKey) {
          FooterComponent = footerModule[footerKey];
        } else if (exportKeys.length > 0) {
          // Fallback: use first exported function
          FooterComponent = footerModule[exportKeys[0]];
        }
      }
      
      if (FooterComponent && typeof FooterComponent === 'function') {
        themes.set(themeId, {
          id: themeId,
          name: config.name,
          config: config,
          colors: `./${themeId}/colors.css`,
          footer: FooterComponent,
        });
      } else {
        logWarning(`Theme ${themeId}: Footer component not found or invalid`);
      }
    } catch (error) {
      logWarning(`Failed to load theme ${themeId}`, error);
    }
  });
}

// Initialize themes on module load
initializeThemes();

// Warn if no themes were discovered
if (process.env.NODE_ENV === 'development') {
  const availableThemes = Array.from(themes.values());
  if (availableThemes.length === 0) {
    logWarning('⚠️ No themes were discovered! Check that theme folders contain Footer.tsx files.');
  }
}

/**
 * Get theme by ID.
 */
export const getTheme = (themeId: string): Theme | undefined => {
  return themes.get(themeId);
};

/**
 * Get all available themes.
 */
export const getAvailableThemes = (): Theme[] => {
  return Array.from(themes.values());
};

/**
 * Get default theme (professional).
 * Always returns a valid Theme, creating a minimal fallback if no themes are loaded.
 */
export const getDefaultTheme = (): Theme => {
  // Try to get professional theme first
  const professionalTheme = themes.get('professional');
  if (professionalTheme) {
    return professionalTheme;
  }
  
  // Try to get any available theme
  const availableThemes = Array.from(themes.values());
  if (availableThemes.length > 0) {
    return availableThemes[0];
  }
  
  // Create a minimal fallback theme if no themes are loaded
  // This should rarely happen, but ensures we always return a valid Theme
  const fallbackConfig: ThemeConfig = {
    id: 'fallback',
    name: 'Fallback',
    description: 'Fallback theme when no themes are available',
    version: '1.0.0',
    primaryColor: '#2563eb',
    secondaryColor: '#64748b',
    hasFooter: false,
  };
  
  return {
    id: 'fallback',
    name: 'Fallback',
    config: fallbackConfig,
    colors: '',
    // No footer component for fallback
  };
};

/**
 * Check if theme exists.
 */
export const themeExists = (themeId: string): boolean => {
  return themes.has(themeId);
};

/**
 * Get theme IDs (for dynamic discovery).
 */
export const getThemeIds = (): string[] => {
  return Array.from(themes.keys());
};
